import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  X,
  Hash,
  Type,
  Check,
  ArrowUpNarrowWide,
  ArrowDownNarrowWide,
} from "lucide-react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ValidationError } from "../utils/importValidation";
import { renderFieldIcon } from "../../fields/FieldList";
import { useSelector } from "react-redux";
import { formatCompactAmount } from "../../api/DataTypeUtils";
import { renderConnectionStrength } from "../../grid/CellRenderer";

interface ReviewValuesProps {
  csvRows: Record<string, string>[];
  mappings: Record<string, any[]>;
  validationErrors: ValidationError[];
  entityType: "CONTACT" | "COMPANY" | "SALE";
  onUnmap?: (sourceColumn: string, rawValue: string) => void;
}

type SortBy = "raw" | "mapped" | "count";
type SortOrder = "asc" | "desc";

const ReviewValues: React.FC<ReviewValuesProps> = ({
  csvRows,
  mappings,
  validationErrors: _validationErrors,
  entityType,
  onUnmap,
}) => {
  const { t, i18n } = useTranslation();
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));

  const entityMappings = mappings[entityType] || [];

  // Get mapped fields for this entity
  const mappedFields = entityMappings
    .filter((m: any) => m?.source?.value && m?.destination?.value)
    .map((m: any) => ({
      source: m.source.value,
      sourceLabel: m.source.label,
      destination: m.destination.value,
      destinationLabel: m.destination.label,
      destinationField: fields?.find((f: any) => f.get("id") === m.destination.value),
    }));

  const [selectedColumn, setSelectedColumn] = useState<string | null>(
    mappedFields.length > 0 ? mappedFields[0].source : null
  );
  const [sortBy, setSortBy] = useState<SortBy>("raw");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(["auto-mapped"]));

  // Get field icon
  const getFieldIcon = (field: any) => {
    if (!field) return null;
    const fieldObj = field.toJS ? field.toJS() : field;
    return renderFieldIcon(fieldObj, "w-4 h-4 text-gray-500");
  };

  // Get project currency for formatting
  const projectCurrency =
    useSelector((state: any) => state.projectReducer.getIn(["project", "currency"])) || "USD";

  // Format value based on field type (like CellRenderer.tsx)
  const formatValue = (
    value: string,
    fieldType?: string,
    fieldId?: string
  ): string | React.ReactNode => {
    if (!value) return value;

    // Check if it's a numeric value
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;

    // Connection strength field - render as colored badge
    if (fieldId === "connectionStrength") {
      return renderConnectionStrength(numValue);
    }

    // Currency fields (estimatedRevenue, price, amount, etc.)
    const currencyFields = ["estimatedRevenue", "price", "amount", "revenue"];
    if (
      fieldType === "currency" ||
      (fieldId && currencyFields.some((f) => fieldId.toLowerCase().includes(f)))
    ) {
      return formatCompactAmount(i18n.language, numValue, projectCurrency, value);
    }

    // Integer fields - format with thousand separators, no decimals
    if (fieldType === "integer") {
      return Math.round(numValue).toLocaleString();
    }

    // Double/number fields - format with thousand separators
    if (fieldType === "double" || fieldType === "number") {
      // Remove unnecessary .0 decimals
      const formatted =
        numValue % 1 === 0
          ? Math.round(numValue).toLocaleString()
          : numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
      return formatted;
    }

    // Default: if it looks like a number, format it
    if (!isNaN(numValue) && value.match(/^-?\d+(\.\d+)?$/)) {
      return numValue % 1 === 0
        ? Math.round(numValue).toLocaleString()
        : numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    return value;
  };

  // Get unique raw values for selected column
  const rawValueData = useMemo(() => {
    if (!selectedColumn) return [];

    const valueMap = new Map<string, number>();
    csvRows.forEach((row) => {
      const value = row[selectedColumn] || "";
      if (value) {
        valueMap.set(value, (valueMap.get(value) || 0) + 1);
      }
    });

    const values = Array.from(valueMap.entries()).map(([value, count]) => ({
      rawValue: value,
      count,
      mappedValue: value, // For now, assume 1:1 mapping
    }));

    // Sort values
    const mult = sortOrder === "asc" ? 1 : -1;
    const sorted = [...values].sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case "raw":
          cmp = a.rawValue.localeCompare(b.rawValue);
          break;
        case "mapped":
          cmp = a.mappedValue.localeCompare(b.mappedValue);
          break;
        case "count":
          cmp = a.count - b.count;
          break;
        default:
          return 0;
      }
      return mult * cmp;
    });

    return sorted;
  }, [selectedColumn, csvRows, sortBy, sortOrder]);

  const selectedField = mappedFields.find((f) => f.source === selectedColumn);
  const selectedFieldObj = selectedField?.destinationField;
  const selectedFieldType = selectedFieldObj?.get?.("type") || selectedFieldObj?.type;
  const selectedFieldId = selectedField?.destination;

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  if (mappedFields.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        {t("No fields mapped for {entity}", { entity: entityType })}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="grid grid-cols-[250px_1fr] gap-6 p-6">
        {/* Left Column: Columns selector */}
        <div className="space-y-2">
          <div className="text-sm font-semibold text-gray-900 mb-3">{t("Columns")}</div>
          <div className="space-y-1">
            {mappedFields.map((field) => {
              const isSelected = selectedColumn === field.source;
              const fieldObj = field.destinationField?.toJS() || {
                id: field.destination,
                type: "string",
              };

              return (
                <div
                  key={field.source}
                  onClick={() => setSelectedColumn(field.source)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? "bg-blue-50 border-2 border-blue-500"
                      : "hover:bg-gray-50 border-2 border-transparent"
                  }`}
                >
                  {getFieldIcon(fieldObj)}
                  <span className="text-sm font-medium text-gray-900 flex-1">
                    {field.sourceLabel || field.source}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: Table with Raw data and Mapped value */}
        <div className="space-y-4">
          {/* Header with sort dropdown */}
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900">{t("Raw data")}</div>
            <Menu as="div" className="relative">
              <MenuButton
                type="button"
                className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 outline-none"
              >
                <ArrowUpDown className="w-3.5 h-3.5 shrink-0" />
                <span>
                  {sortBy === "raw" && t("Sorted by Raw value")}
                  {sortBy === "mapped" && t("Sorted by Mapped value")}
                  {sortBy === "count" && t("Sorted by Count")}
                </span>
                <ChevronDown className="w-3.5 h-3.5 shrink-0" />
              </MenuButton>
              <MenuItems
                anchor="bottom end"
                className="z-20 mt-1 min-w-[180px] origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg outline-none"
              >
                <div className="py-1">
                  <div className="px-2 py-1 text-xs font-medium text-gray-500">{t("Sort by")}</div>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => setSortBy("raw")}
                        className={`flex w-full items-center gap-2 px-2 py-1.5 text-sm ${
                          focus ? "bg-gray-100" : ""
                        } ${sortBy === "raw" ? "text-gray-900 font-medium" : "text-gray-700"}`}
                      >
                        <Type className="w-4 h-4 text-gray-400 shrink-0" />
                        {t("Raw value")}
                        {sortBy === "raw" && <Check className="w-4 h-4 text-green-600 ml-auto" />}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => setSortBy("mapped")}
                        className={`flex w-full items-center gap-2 px-2 py-1.5 text-sm ${
                          focus ? "bg-gray-100" : ""
                        } ${sortBy === "mapped" ? "text-gray-900 font-medium" : "text-gray-700"}`}
                      >
                        <Type className="w-4 h-4 text-gray-400 shrink-0" />
                        {t("Mapped value")}
                        {sortBy === "mapped" && (
                          <Check className="w-4 h-4 text-green-600 ml-auto" />
                        )}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => setSortBy("count")}
                        className={`flex w-full items-center gap-2 px-2 py-1.5 text-sm ${
                          focus ? "bg-gray-100" : ""
                        } ${sortBy === "count" ? "text-gray-900 font-medium" : "text-gray-700"}`}
                      >
                        <Hash className="w-4 h-4 text-gray-400 shrink-0" />
                        {t("Row count")}
                        {sortBy === "count" && <Check className="w-4 h-4 text-green-600 ml-auto" />}
                      </button>
                    )}
                  </MenuItem>
                </div>
                <div className="border-t border-gray-100 py-1">
                  <div className="px-2 py-1 text-xs font-medium text-gray-500">{t("Order")}</div>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => setSortOrder("asc")}
                        className={`flex w-full items-center gap-2 px-2 py-1.5 text-sm ${
                          focus ? "bg-gray-100" : ""
                        } ${sortOrder === "asc" ? "text-gray-900 font-medium" : "text-gray-700"}`}
                      >
                        <ArrowUpNarrowWide className="w-4 h-4 text-gray-400 shrink-0" />
                        {t("Ascending")}
                        {sortOrder === "asc" && (
                          <Check className="w-4 h-4 text-green-600 ml-auto" />
                        )}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => setSortOrder("desc")}
                        className={`flex w-full items-center gap-2 px-2 py-1.5 text-sm ${
                          focus ? "bg-gray-100" : ""
                        } ${sortOrder === "desc" ? "text-gray-900 font-medium" : "text-gray-700"}`}
                      >
                        <ArrowDownNarrowWide className="w-4 h-4 text-gray-400 shrink-0" />
                        {t("Descending")}
                        {sortOrder === "desc" && (
                          <Check className="w-4 h-4 text-green-600 ml-auto" />
                        )}
                      </button>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>
          </div>

          {/* Collapsible section header */}
          <div>
            <button
              onClick={() => toggleSection("auto-mapped")}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-gray-900 mb-3"
            >
              <span>
                {t("Automatically mapped")} {rawValueData.length}
              </span>
              {expandedSections.has("auto-mapped") ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
            </button>

            {/* Table with Raw data and Mapped value */}
            {expandedSections.has("auto-mapped") && rawValueData.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                        {t("Raw data")}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                        {t("Mapped value")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rawValueData.map((item) => (
                      <tr key={item.rawValue} className="hover:bg-gray-50">
                        {/* Raw data column */}
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatValue(item.rawValue, selectedFieldType, selectedFieldId)}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {item.count} {item.count === 1 ? t("row") : t("rows")}
                            </div>
                          </div>
                        </td>
                        {/* Mapped value column */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400">→</span>
                            <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1.5 flex-1 min-w-0">
                              {selectedFieldObj && getFieldIcon(selectedFieldObj)}
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {formatValue(item.mappedValue, selectedFieldType, selectedFieldId)}
                              </span>
                            </div>
                            {onUnmap && (
                              <button
                                onClick={() => onUnmap(selectedColumn!, item.rawValue)}
                                className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                                title={t("Unmap")}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {rawValueData.length === 0 && (
              <div className="text-sm text-gray-500 py-4 text-center">
                {t("No data available for this column")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewValues;
